/**
 * Google's rating for a place, looked up live in the browser when someone opens a place on the Suburbs map (#253
 * step 3; Ivan 2026-10-16). Shown, never stored: Google's terms allow keeping only the place ID. Uses the Maps
 * JavaScript library the client already loads (src/index.html, libraries=places); one Place Details request per place
 * opened (billed per request), cached for the session. A place without a Google place ID is found by name near its
 * position first.
 */

declare const google: any;

export interface GoogleFacts {
  placeId: string;
  name: string | null;
  rating: number | null;
  total: number | null;
  /** OPERATIONAL, CLOSED_TEMPORARILY, CLOSED_PERMANENTLY */
  status: string | null;
  url: string | null;
}

/** 'unavailable': Google refused (key, referrer, quota) or isn't loaded; null: Google has no such place. */
export type GoogleLookup = GoogleFacts | null | 'unavailable';

const cache = new Map<number, Promise<GoogleLookup>>();

function service(): any | null {
  const w: any = window as any;
  if (w.__gmAuthFailed || !w.google?.maps?.places?.PlacesService) return null;
  return new google.maps.places.PlacesService(document.createElement('div'));
}

const refused = (status: string) => !['OK', 'ZERO_RESULTS', 'NOT_FOUND'].includes(status);

function findPlaceId(
  svc: any,
  name: string,
  lat: number,
  lng: number
): Promise<string | null | 'unavailable'> {
  return new Promise((resolve) =>
    svc.findPlaceFromQuery(
      { query: name, fields: ['place_id'], locationBias: { center: { lat, lng }, radius: 300 } },
      (results: any[] | null, status: string) =>
        resolve(refused(status) ? 'unavailable' : (results?.[0]?.place_id ?? null))
    )
  );
}

function details(svc: any, placeId: string): Promise<GoogleLookup> {
  return new Promise((resolve) =>
    svc.getDetails(
      { placeId, fields: ['name', 'rating', 'user_ratings_total', 'business_status', 'url'] },
      (r: any, status: string) => {
        if (refused(status)) return resolve('unavailable');
        if (!r) return resolve(null);
        resolve({
          placeId,
          name: r.name ?? null,
          rating: typeof r.rating === 'number' ? r.rating : null,
          total: typeof r.user_ratings_total === 'number' ? r.user_ratings_total : null,
          status: r.business_status ?? null,
          url: r.url ?? null,
        });
      }
    )
  );
}

export function googleFacts(place: {
  locationId: number;
  googlePlaceId?: string | null;
  name: string;
  lat: number;
  lng: number;
}): Promise<GoogleLookup> {
  const hit = cache.get(place.locationId);
  if (hit) return hit;
  const run = (async (): Promise<GoogleLookup> => {
    const svc = service();
    if (!svc) return 'unavailable';
    const id = place.googlePlaceId || (await findPlaceId(svc, place.name, place.lat, place.lng));
    if (id === 'unavailable') return 'unavailable';
    return id ? details(svc, id) : null;
  })().catch((): GoogleLookup => 'unavailable');
  cache.set(place.locationId, run);
  // A refusal can be fixed (the key's referrers) without a reload: don't cache it.
  run.then((r) => r === 'unavailable' && cache.delete(place.locationId));
  return run;
}

/** ★★★★☆ for 4.2 (rounded to the nearest half, shown as whole stars plus a half). */
export function stars(rating: number): string {
  const r = Math.round(rating * 2) / 2;
  const full = Math.floor(r);
  return '★'.repeat(full) + (r - full ? '½' : '') + '☆'.repeat(5 - Math.ceil(r));
}
